import {
  ReactNode,
  useState,
  ReactElement,
  KeyboardEvent,
  MouseEvent,
} from 'react';

import Menu from '@mui/material/Menu';

import { Button, ButtonSize } from '../Button';
import { Icon, IconColor } from '../Icon';

import { useStyles } from './DropdownButton.styles';

/**
 * Свойства компонента {@link DropdownButton}
 */
export interface DropdownButtonProps {
  /**
   * Вариант кнопки.
   */
  variant: 'primary' | 'secondary' | 'tertiaryLight' | 'quaternary';

  /**
   * Вертикальное положение меню относительно кнопки.
   */
  verticalPosition: 'top' | 'bottom';

  /**
   * Горизонтальное положение меню относительно кнопки.
   */
  horizontalPosition: 'left' | 'right';

  /**
   * Элементы, отображаемые в выпадающем меню.
   */
  menuItems: ReactNode | ReactNode[];

  /**
   * Если true, кнопка будет отключена.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Колбэк-функция для обработки открытия меню.
   */
  onOpen?(): void;

  /**
   * Колбэк-функция для обработки закрытия меню.
   */
  onClose?(): void;

  /**
   * Тестовый ID для кнопки.
   */
  testId?: string;

  /**
   * Пользовательская кнопка.
   */
  button?: (options: {
    onClick: (event: KeyboardEvent | MouseEvent) => void;
    isOpened: boolean;
    isDisabled: boolean;
  }) => ReactElement;

  /**
   * Размер кнопки.
   *
   * @defaultValue `'small'`
   */
  size?: ButtonSize;

  /**
   * Если true, используется горизонтальная иконка (more_horizontal) вместо вертикальной (more_vertical).
   */
  horizonIcon?: boolean;

  /**
   * DOM-элемент, в который будет смонтировано меню. Нужен при рендере внутри shadow DOM (виджет),
   * чтобы стили меню применялись внутри shadow root, а не в document.body.
   */
  container?: HTMLElement | null;
}

const iconColors: Record<
  'primary' | 'secondary' | 'tertiaryLight' | 'quaternary',
  IconColor
> = {
  primary: 'bg.light.primary',
  secondary: 'original.brand.primary',
  tertiaryLight: 'text.light.primary',
  quaternary: 'original.brand.primary',
};

/**
 * Компонент для отображения кнопки с выпадающим меню.
 *
 * @public
 * @param variant - Вариант кнопки.
 * @param verticalPosition - Вертикальное положение меню относительно кнопки.
 * @param horizontalPosition - Горизонтальное положение меню относительно кнопки.
 * @param menuItems - Элементы, отображаемые в выпадающем меню.
 * @param disabled - Если true, кнопка будет отключена.
 * @param onOpen - Колбэк-функция для обработки открытия меню.
 * @param onClose - Колбэк-функция для обработки закрытия меню.
 * @param testId - Тестовый ID для кнопки.
 * @param button - Пользовательская кнопка.
 * @param size - Размер кнопки.
 * @param horizonIcon - Если true, используется горизонтальная иконка (more_horizontal) вместо вертикальной (more_vertical).
 * @param container - чтобы стили меню применялись внутри shadow root, а не в document.body.
 * @returns JSX-элемент, представляющий кнопку с выпадающим меню и связанным с ней меню.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link DropdownButtonProps}
 */
export const DropdownButton = ({
  variant,
  menuItems,
  verticalPosition,
  horizontalPosition,
  onOpen,
  onClose,
  disabled = false,
  button,
  testId,
  size = 'small',
  horizonIcon,
  container,
}: DropdownButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<null | (EventTarget & Element)>(
    null,
  );

  const { classes } = useStyles();

  const handleOpen = (event: KeyboardEvent | MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };

  const handleClose = (event: KeyboardEvent | MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
    onClose?.();
  };

  const icon = (
    <Icon
      name={horizonIcon ? 'more_horizontal' : 'more_vertical'}
      size={size === 'small' || size === 'extraSmall' ? '16' : '24'}
      color={iconColors[variant]}
      data-testid="dropdown-button-more-vert-icon"
    />
  );

  if (disabled) {
    return button ? (
      <>
        {button({
          onClick: handleOpen,
          isOpened: !!anchorEl,
          isDisabled: disabled,
        })}
      </>
    ) : (
      <Button
        variant={variant}
        size={size}
        icon={icon}
        disabled={disabled}
        onClick={handleOpen}
        disableRipple
      />
    );
  }

  return (
    <>
      {button ? (
        <>
          {button({
            onClick: handleOpen,
            isOpened: !!anchorEl,
            isDisabled: disabled,
          })}
        </>
      ) : (
        <Button
          variant={variant}
          size={size}
          testId={testId}
          icon={icon}
          onClick={handleOpen}
          disableRipple
        />
      )}
      <Menu
        container={container}
        classes={{
          list: classes.menuList,
          root: classes.rootPaper,
          paper: `${verticalPosition === 'bottom' ? classes.paper : ''}`,
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: verticalPosition,
          horizontal: horizontalPosition,
        }}
        transformOrigin={{
          vertical: verticalPosition === 'top' ? 'bottom' : 'top',
          horizontal: horizontalPosition,
        }}
        transitionDuration={{
          appear: 200,
          enter: 200,
          exit: 0,
        }}
        data-testid="dropdownButton"
      >
        <div onClick={handleClose}>{menuItems}</div>
      </Menu>
    </>
  );
};
